import React, { forwardRef, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PrintableInvoice from "@/components/PrintableInvoice";

const toNum = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * printInvoice opens a clean popup window with ONLY the bill content + minimal
 * CSS. This avoids the shadcn Dialog portal / visibility:hidden cascade issue.
 */
export function printInvoice({ bill, settings, mode = "a4", logoDataUrl }) {
  if (!bill) return;
  const win = window.open("", "_blank", "width=800,height=900");
  if (!win) {
    alert("Allow popups to print");
    return;
  }
  const isThermal = mode === "thermal";
  const items = (bill.items || [])
    .map(
      (it) => `
    <tr>
      <td>${escapeHtml(it.name)}<br><span class="muted">${escapeHtml(it.purity || "")}</span></td>
      <td class="num">${toNum(it.net_weight).toFixed(3)}</td>
      <td class="num">${toNum(it.rate_per_gram).toFixed(0)}</td>
      <td class="num">${toNum(it.quantity)}</td>
      <td class="num">${toNum(it.amount).toFixed(2)}</td>
    </tr>
  `,
    )
    .join("");

  const totalsRows = [
    ["Subtotal", toNum(bill.sub_total)],
    toNum(bill.discount) > 0 ? ["Discount", -toNum(bill.discount)] : null,
    [`GST (${toNum(bill.gst_percent)}%)`, toNum(bill.gst_amount)],
    toNum(bill.old_gold_value) > 0
      ? ["Less: Old gold", -toNum(bill.old_gold_value)]
      : null,
    toNum(bill.round_off) ? ["Round off", toNum(bill.round_off)] : null,
  ]
    .filter(Boolean)
    .map(
      ([l, v]) =>
        `<div class="row"><span>${l}</span><span class="num">${inr(v)}</span></div>`,
    )
    .join("");

  const paymentsRows = (bill.payments || [])
    .filter((p) => toNum(p.amount) > 0)
    .map(
      (p) =>
        `<div class="row"><span>${escapeHtml((p.mode || "cash").toUpperCase())}${p.reference ? ` · ${escapeHtml(p.reference)}` : ""}${p.paid_at ? ` <span class="muted">${escapeHtml(String(p.paid_at).slice(0, 10))}</span>` : ""}</span><span class="num">${inr(toNum(p.amount))}</span></div>`,
    )
    .join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(bill.invoice_no || "Invoice")}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Helvetica','Arial',sans-serif;color:#000;background:#fff;padding:24px;}
  .wrap{max-width:${isThermal ? 320 : 760}px;margin:0 auto;font-size:${isThermal ? 11 : 13}px;}
  .center{text-align:center;}
  .store{font-size:${isThermal ? 16 : 22}px;font-weight:700;letter-spacing:1px;}
  .meta{font-size:${isThermal ? 9 : 11}px;}
  .logo{max-height:${isThermal ? 40 : 64}px;margin:0 auto 8px;display:block;}
  .divider{border-top:1px dashed #000;margin:8px 0;}
  table{width:100%;border-collapse:collapse;margin:6px 0;}
  th{border-bottom:1px solid #000;text-align:left;padding:4px 6px;font-size:${isThermal ? 10 : 12}px;}
  td{padding:4px 6px;font-size:${isThermal ? 10 : 12}px;border-bottom:1px dotted #999;vertical-align:top;}
  td.num,th.num{text-align:right;font-variant-numeric:tabular-nums;}
  .row{display:flex;justify-content:space-between;padding:2px 0;}
  .total{border-top:1px solid #000;margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;font-size:${isThermal ? 13 : 16}px;font-weight:700;}
  .muted{color:#666;font-size:${isThermal ? 9 : 10}px;}
  .footer{margin-top:14px;text-align:center;font-size:${isThermal ? 9 : 11}px;}
  .sect-title{font-weight:600;font-size:${isThermal ? 10 : 12}px;margin-top:8px;text-transform:uppercase;letter-spacing:1px;}
  @media print {
    body{padding:0;}
    @page { margin: ${isThermal ? "4mm" : "12mm"}; size: ${isThermal ? "80mm auto" : "A4"}; }
  }
</style>
</head><body>
  <div class="wrap">
    <div class="center">
      ${logoDataUrl ? `<img class="logo" src="${logoDataUrl}" alt=""/>` : ""}
      <div class="store">${escapeHtml(settings?.store_name || "JEWELLERY STORE")}</div>
      <div class="meta">${escapeHtml(settings?.address || "")}</div>
      <div class="meta">${settings?.phone ? "Ph: " + escapeHtml(settings.phone) : ""}${settings?.gst_number ? "  GSTIN: " + escapeHtml(settings.gst_number) : ""}</div>
    </div>
    <div class="divider"></div>
    <div class="row"><span><b>Invoice:</b> ${escapeHtml(bill.invoice_no)}</span><span>${new Date(bill.created_at).toLocaleString("en-IN")}</span></div>
    <div><b>Customer:</b> ${escapeHtml(bill.customer_name || "Walk-in")}${bill.customer_mobile ? " · " + escapeHtml(bill.customer_mobile) : ""}</div>
    <div class="divider"></div>
    <table>
      <thead><tr><th>Item</th><th class="num">Wt(g)</th><th class="num">Rate</th><th class="num">Qty</th><th class="num">Amount</th></tr></thead>
      <tbody>${items}</tbody>
    </table>
    ${totalsRows}
    <div class="total"><span>TOTAL</span><span class="num">${inr(toNum(bill.grand_total))}</span></div>
    ${paymentsRows ? `<div class="sect-title">Payments</div>${paymentsRows}` : ""}
    <div class="row"><span>Paid</span><span class="num">${inr(toNum(bill.paid))}</span></div>
    ${toNum(bill.balance) > 0 ? `<div class="row" style="font-weight:700"><span>Balance Due</span><span class="num">${inr(toNum(bill.balance))}</span></div>` : ""}
    <div class="footer">${escapeHtml(settings?.invoice_footer || "Thank you!")}</div>
  </div>
  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 250);
    };
  </script>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

function escapeHtml(s) {
  if (s == null) return "";
  return String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
}

function inr(n) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

/**
 * Fetch logo as data URL so the popup window can render it without
 * sending auth cookies (popup is a different origin context).
 */
export async function logoAsDataUrl(url) {
  if (!url) return null;
  try {
    const { imageSrc } = await import("@/components/ImageUpload");
    const resolved = imageSrc(url);
    const res = await fetch(resolved);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}
